// ============================================================
// CqcAiCheckPanel.jsx
// Shows AI compliance analysis for a cqc_content record.
// Place at: src/components/cqc/CqcAiCheckPanel.jsx
//
// Used inside the admin Document Master (CqcContent edit area)
// Props:
//   contentId  - INT  - cqc_content.id
//   analysis   - OBJ  - existing analysis (from fetchcontent response), can be null
// ============================================================

import { useState } from "react";
import { Button, Alert, Spinner, Badge } from "react-bootstrap";
import axios from "axios";
import Utils from "../../common/Utils";

const CqcAiCheckPanel = ({ contentId, analysis: initialAnalysis }) => {
  const [analysis,  setAnalysis]  = useState(initialAnalysis || null);
  const [checking,  setChecking]  = useState(false);
  const [error,     setError]     = useState(null);
  const [open,      setOpen]      = useState(!!initialAnalysis);

  const runCheck = async () => {
    setChecking(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('content_id', contentId);
      const res = await axios.post(Utils.apiUrl('cqcs/ai_check'), fd, Utils.apiHeaders());
      if (res.data.success) {
        setAnalysis(res.data.analysis);
        setOpen(true);
      } else {
        setError(res.data.message);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setChecking(false);
    }
  };

  const flagColors = { critical: '#dc3545', warning: '#fd7e14', info: '#0d6efd', ok: '#198754' };
  const flagBgs    = { critical: '#f8d7da', warning: '#fff3cd', info: '#cff4fc', ok: '#d1e7dd' };
  const flagIcons  = { critical: '🚨', warning: '⚠️', info: 'ℹ️', ok: '✅' };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Button size="sm" variant={analysis ? 'outline-secondary' : 'outline-primary'}
          onClick={analysis ? () => setOpen(!open) : runCheck}
          disabled={checking}
          style={{ fontSize: 11 }}>
          {checking ? <><Spinner size="sm" className="me-1" />Checking…</> : analysis ? (open ? 'Hide Analysis' : '✦ Show Analysis') : '✦ AI Check'}
        </Button>
        {analysis && !open && (
          <Button size="sm" variant="outline-secondary" onClick={runCheck} disabled={checking} style={{ fontSize: 11 }}>
            Re-check
          </Button>
        )}
        {analysis?.score !== undefined && (
          <Badge bg={analysis.score >= 75 ? 'success' : analysis.score >= 50 ? 'warning' : 'danger'} style={{ fontSize: 11 }}>
            {analysis.score}% compliant
          </Badge>
        )}
      </div>

      {error && <Alert variant="danger" className="py-1 px-2" style={{ fontSize: 12 }}>{error}</Alert>}

      {open && analysis && (
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, border: '1px solid #dee2e6' }}>
          {analysis.summary && (
            <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10, padding: '6px 10px', background: '#fff', borderRadius: 6 }}>
              {analysis.summary}
              {analysis.cqc_version && (
                <span style={{ fontSize: 10, color: '#adb5bd', marginLeft: 8 }}>
                  [{analysis.cqc_version} · {analysis.htm_version}]
                </span>
              )}
            </div>
          )}
          {(analysis.flags || []).map((f, i) => (
            <div key={i} style={{ background: flagBgs[f.type] || '#f8f9fa', borderLeft: '3px solid ' + (flagColors[f.type] || '#6c757d'), borderRadius: 6, padding: '9px 12px', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>
                {flagIcons[f.type]} {f.title}
                {f.regulation && <span style={{ color: '#0e7c7b', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>[{f.regulation}]</span>}
              </div>
              {f.current  && <div style={{ fontSize: 11, marginTop: 3 }}><strong>Current:</strong> {f.current}</div>}
              {f.required && <div style={{ fontSize: 11, marginTop: 3, color: '#1e8a5e', fontStyle: 'italic' }}>✏ Required: {f.required}</div>}
              {f.reason && <div style={{ fontSize: 11, marginTop: 3, color: '#495057' }}>ℹ Explanation: {f.reason}</div>}
              {f.section  && <div style={{ fontSize: 10, color: '#6c757d', marginTop: 2 }}>📍 {f.section}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CqcAiCheckPanel;
